import { Link, useRouterState } from "@tanstack/react-router";
import { MenuItem } from "../../data/menuItems";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../ui/navigation-menu";
import { twMerge } from "tailwind-merge";

interface DesktopNavigationProps {
  menuItems: MenuItem[];
}

export function DesktopNavigation({ menuItems }: DesktopNavigationProps) {
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  // Use fluid typography for responsive scaling
  const linkStyle =
    "flex items-center rounded-lg px-3 py-2 fluid-menu-text xl:px-4 font-semibold leading-6 text-white/80 hover:bg-dark hover:text-white transition-all duration-200 cursor-pointer select-none bg-transparent border-0 outline-none text-left whitespace-nowrap";

  // Helper to determine if a route is active in our SPA
  const checkActive = (href?: string) => {
    if (!href) return false;
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  return (
    <div className="hidden lg:flex lg:gap-6 xl:gap-12 lg:justify-end">
      <NavigationMenu viewport={false}>
        <NavigationMenuList className="flex gap-4 xl:gap-8">
          {menuItems.map((item) => {
            if (item.type === "group") {
              const isAnyChildActive = item.children?.some(subItem => checkActive(subItem.href)) || false;

              return (
                <NavigationMenuItem key={item.name} className="relative">
                  <NavigationMenuTrigger
                    className={twMerge(
                      linkStyle,
                      "hover:bg-dark hover:text-white data-[state=open]:bg-dark data-[state=open]:text-white focus:bg-transparent [&_svg]:text-white [&_svg]:hover:text-white [&_svg]:size-5 [&_svg]:ml-2",
                      isAnyChildActive && "text-white bg-dark"
                    )}
                  >
                    {item.name}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="absolute left-0 top-full mt-2 min-w-max rounded-xl bg-white p-2 shadow-2xl ring-1 ring-black/5 flex flex-col gap-1 z-[100]">
                    {item.children?.map((subItem) => {
                      const isSubActive = checkActive(subItem.href);

                      return (
                        <NavigationMenuLink key={subItem.name} asChild>
                          <Link
                            to={subItem.href || "/"}
                            className={twMerge(
                              "block px-4 py-2.5 text-xl font-semibold text-gray-800 rounded-lg hover:bg-gray-100 hover:text-darkred transition-colors duration-150 whitespace-nowrap",
                              isSubActive && "bg-gray-50 text-darkred font-bold"
                            )}
                          >
                            {subItem.name}
                          </Link>
                        </NavigationMenuLink>
                      );
                    })}
                  </NavigationMenuContent>
                </NavigationMenuItem>
              );
            }

            if (item.type === "icon") {
              const isItemActive = checkActive(item.href);
              const content = (
                <>
                  <img
                    src={item.img}
                    className="size-12 object-contain drop-shadow transition-transform active:scale-95 shrink-0"
                    alt={item.name}
                  />
                  <span className="sr-only">{item.name}</span>
                </>
              );

              return (
                <NavigationMenuItem key={item.name} className="shrink-0">
                  <NavigationMenuLink asChild>
                    {item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={twMerge(linkStyle, "p-1.5 hover:bg-dark shrink-0")}
                      >
                        {content}
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    ) : (
                      <Link
                        to={item.href || "/"}
                        className={twMerge(
                          linkStyle,
                          "p-1.5 hover:bg-dark shrink-0",
                          isItemActive && "text-white bg-dark"
                        )}
                      >
                        {content}
                      </Link>
                    )}
                  </NavigationMenuLink>
                </NavigationMenuItem>
              );
            }

            if (item.external) {
              return (
                <NavigationMenuItem key={item.name}>
                  <NavigationMenuLink asChild>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkStyle}
                    >
                      {item.name}
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              );
            }

            const isActive = checkActive(item.href);

            return (
              <NavigationMenuItem key={item.name}>
                <NavigationMenuLink asChild>
                  <Link
                    to={item.href || "/"}
                    className={twMerge(linkStyle, isActive && "text-white bg-dark")}
                  >
                    {item.name}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            );
          })}
        </NavigationMenuList>
      </NavigationMenu>
    </div>
  );
}